import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Form, Input, Select, Button, Table, Card, Row, Col,
  Space, Tag, theme, Popconfirm, Breadcrumb
} from 'antd';
import axios from 'axios';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import { AlertError, AlertSuccess } from '../usableclasses.js';

const { Option } = Select;
const { Content } = Layout;

const PurchaseOrderPage = () => {
  const navigate = useNavigate();
  const ApiURL = MAINAPIURL;
  const TOKEN = localStorage.getItem("AUTHCODE");
  const USERID = localStorage.getItem("USERID") || "";

  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();
  const [form] = Form.useForm();

  // --- State ---
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    financialYear: '',
    year: '',
    month: '',
    vendor: '',
    status: '',
    sortField: '',
    sortMethod: ''
  });

  const [filteredData, setFilteredData] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 5;

  // --- Helpers ---
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? dateString : date.toLocaleDateString();
  }, []);

  const getStatusBadge = useCallback((status) => {
    const map = {
      DRAFT: { color: 'orange', text: 'Draft' },
      PENDING: { color: 'blue', text: 'Pending' },
      APPROVED: { color: 'green', text: 'Approved' },
      REJECTED: { color: 'red', text: 'Rejected' },
      DELETED: { color: 'red', text: 'Deleted' },
    };
    const s = (status || '').toUpperCase();
    return <Tag color={map[s]?.color || 'default'}>{map[s]?.text || status}</Tag>;
  }, []);

  // --- Effects ---
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setYearList(Array.from({ length: 10 }, (_, i) => (currentYear - i).toString()));
  }, []);

  useEffect(() => {
    const fetchFinancialYears = async () => {
      setLoadingFinancialYears(true);
      try {
        const { data } = await axios.get(`${ApiURL}/master/financeyear/dropdown`, {
          headers: { SystemAccessToken: TOKEN }
        });
        setFinancialYears(Array.isArray(data) ? data : []);
      } catch {
        AlertError('Failed to load financial years');
      } finally {
        setLoadingFinancialYears(false);
      }
    };
    fetchFinancialYears();
  }, [ApiURL, TOKEN]);

  useEffect(() => {
    const fetchVendors = async () => {
      setLoadingVendors(true);
      try {
        const { data } = await axios.get(`${ApiURL}/vendor/dropdown`, {
          headers: { SystemAccessToken: TOKEN }
        });
        setVendors(Array.isArray(data) ? data.filter(v => v?.label) : []);
      } catch {
        AlertError('Failed to load vendors');
      } finally {
        setLoadingVendors(false);
      }
    };
    fetchVendors();
  }, [ApiURL, TOKEN]);

  useEffect(() => {
    handleFilter(searchParams, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handlers ---
  const handleFilter = useCallback(async (values, pageNum = 1) => {
    setLoading(true);
    try {
      const requestParams = {
        keyword: values.keyword || '',
        finYear: values.financialYear || '',
        sysYear: values.year || '',
        sysMonth: values.month || '',
        vendorId: values.vendor || '',
        currentStatus: values.status || '',
        pageLength: rowsPerPage,
        pageNumber: pageNum,
        orderColumn: values.sortField || '',
        orderBy: values.sortMethod || ''
      };

      const { data } = await axios.post(`${ApiURL}/purchase/polistfilter`, requestParams, {
        headers: { SystemAccessToken: TOKEN }
      });

      let records = [];
      let total = 0;
      if (data?.dataList) {
        records = data.dataList;
        total = data.rowcount || records.length;
      } else if (Array.isArray(data)) {
        records = data;
        total = data.length;
      } else if (data?.records) {
        records = data.records;
        total = data.totalRecords || records.length;
      } else if (data?.data) {
        records = data.data;
        total = data.total || records.length;
      } else if (data?.poId) {
        records = [data];
        total = 1;
      }

      const processed = records.map(r => ({
        poId: r.poId || '',
        poReferance: r.poReferance || '',
        finYear: r.finYear || '',
        sysYear: r.sysYear || '',
        sysMonth: r.sysMonth || '',
        createDate: r.createDate || '',
        vendorName: r.vendorName || '',
        vendorBillNo: r.vendorBillNo || '',
        totalCost: r.totalCost || '0.00',
        totalPaid: r.totalPaid || '0.00',
        totalPayable: r.totalPayable || '0.00',
        currentStage: r.currentStage || '',
        createdBy: r.createdBy || '',
        createdDate: r.createdDate || ''
      }));

      setFilteredData(processed);
      setTotalRecords(total);
      setCurrentPage(pageNum);
      setSearchParams(values);
    } catch (error) {
      AlertError('Failed to filter purchase orders: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [ApiURL, TOKEN, rowsPerPage]);

  const handleClear = useCallback(() => {
    form.resetFields();
    const defaults = {
      keyword: '',
      financialYear: '',
      year: '',
      month: '',
      vendor: '',
      status: '',
      sortField: '',
      sortMethod: ''
    };
    handleFilter(defaults, 1);
  }, [form, handleFilter]);

  const handlePageChange = useCallback((page) => {
    form.validateFields().then(values => handleFilter(values, page));
  }, [form, handleFilter]);

  const handleDelete = async (record) => {
    setLoading(true);
    try {
      await axios.post(`${ApiURL}/purchase/delete`, {
        PoId: record.poId,
        DeletedBy: USERID
      }, { headers: { SystemAccessToken: TOKEN } });
      AlertSuccess('Purchase order deleted successfully');
      handleFilter(form.getFieldsValue(), currentPage);
    } catch (error) {
      AlertError('Failed to delete purchase order: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // --- Table Columns ---
  const columns = useMemo(() => [
    {
      title: 'PO Reference',
      dataIndex: 'poReferance',
      sorter: (a, b) => a.poReferance?.localeCompare(b.poReferance || ''),
    },
    {
      title: 'Create Date',
      dataIndex: 'createDate',
      render: formatDate,
      sorter: (a, b) => new Date(a.createDate) - new Date(b.createDate),
    },
    {
      title: 'Status',
      dataIndex: 'currentStage',
      render: getStatusBadge,
      sorter: (a, b) => (a.currentStage || '').localeCompare(b.currentStage || ''),
    },
    {
      title: 'PO Value',
      dataIndex: 'totalCost',
      render: (v) => v ? `${parseFloat(v).toLocaleString()}` : '0.00',
      sorter: (a, b) => parseFloat(a.totalCost || 0) - parseFloat(b.totalCost || 0),
    },
    { title: 'Vendor', dataIndex: 'vendorName' },
    { title: 'Year', dataIndex: 'sysYear' },
    {
      title: 'Month',
      dataIndex: 'sysMonth',
      render: (m) => {
        const names = ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'];
        const idx = parseInt(m, 10) - 1;
        return names[idx] || m;
      }
    },
    { title: 'Vendor Bill No', dataIndex: 'vendorBillNo' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const status = (record.currentStage || '').toUpperCase();
        return (
          <Space size="small">
            {status !== 'DRAFT' && (
              <>
                <Button type="primary" size="small" onClick={() => navigate(`/poview/${record.poId}`)}>View</Button>
                <Button size="small" color='purple' variant='solid' onClick={() => navigate(`/poprint/${record.poId}`)}>Print</Button>
              </>
            )}
            {status === 'DRAFT' && (
              <>
                <Button
                  size="small"
                  variant='outlined'
                  color='yellow'
                  onClick={() => navigate(`/poedit/${record.poId}`)}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Are you sure you want to delete this purchase order?"
                  onConfirm={() => handleDelete(record)}
                >
                  <Button danger size="small">Delete</Button>
                </Popconfirm>
              </>
            )}
            {status === 'PENDING' && (
              <Popconfirm
                title="Are you sure you want to delete this purchase order?"
                onConfirm={() => handleDelete(record)}
              >
                <Button danger size="small">Delete</Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ], [formatDate, getStatusBadge, handleDelete, navigate]);

  const paginationConfig = useMemo(() => ({
    current: currentPage,
    pageSize: rowsPerPage,
    total: totalRecords,
    onChange: handlePageChange,
    showSizeChanger: false,
    showTotal: (t, r) => `Showing ${r[0]}-${r[1]} of ${t} items`
  }), [currentPage, totalRecords, rowsPerPage, handlePageChange]);

  return (
    <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
      <Headerdiv />
      <Content style={{ padding: '10px' }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item>Purchase Orders List</Breadcrumb.Item>
        </Breadcrumb>

        <div style={{ padding: 20, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
          <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Search Purchase Orders" style={{ marginBottom: 16 }}>
                  <Form
                    form={form}
                    name="search_form"
                    layout="vertical"
                    onFinish={handleFilter}
                    initialValues={searchParams}
                  >
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={12} md={4}>
                        <Form.Item name="keyword" label="Search Keyword">
                          <Input placeholder="Search..." />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="financialYear" label="Financial Year">
                          <Select placeholder="Select Financial Year" allowClear loading={loadingFinancialYears}>
                            {financialYears.map(year => (
                              <Option key={year.id} value={year.name}>{year.name}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="year" label="Year">
                          <Select placeholder="Select Year" allowClear>
                            {yearList.map((y, i) => (
                              <Option key={i} value={y}>{y}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="month" label="Month">
                          <Select placeholder="Select Month" allowClear>
                            {[
                              "January","February","March","April","May","June",
                              "July","August","September","October","November","December"
                            ].map((m, i) => (
                              <Option key={i} value={(i+1).toString().padStart(2,"0")}>{m}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={5}>
                        <Form.Item name="status" label="Status">
                          <Select placeholder="Select Status" allowClear>
                            {["DRAFT","PENDING","APPROVED","REJECTED","DELETED"].map(s => (
                              <Option key={s} value={s}>{s}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[16, 8]} align="bottom">
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="vendor" label="Vendor">
                          <Select placeholder="Select Vendor" allowClear loading={loadingVendors}>
                            {vendors.map((v, i) => (
                              <Option key={i} value={v.id}>{v.label}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="sortField" label="Sort By">
                          <Select placeholder="Select Field">
                            <Option value="REFERANCE">PO Reference</Option>
                            <Option value="CREATED_DATE">Create Date</Option>
                            <Option value="FINANCE_YEAR">Finance Year</Option>
                            <Option value="COST">Total Cost</Option>
                            <Option value="PAID_AMOUNT">Paid Amount</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item name="sortMethod" label="Sort Order">
                          <Select placeholder="Select Order">
                            <Option value="ASC">Ascending</Option>
                            <Option value="DESC">Descending</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item>
                          <Space style={{ width: "100%", justifyContent: "flex-start" }}>
                            <Button type="primary" htmlType="submit">Filter</Button>
                            <Button onClick={handleClear}>Clear</Button>
                          </Space>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>

                <Card title="Purchase Order List">
                  <div style={{ overflowX: 'auto' }}>
                    <Table
                      columns={columns}
                      dataSource={filteredData}
                      pagination={paginationConfig}
                      rowKey="poId"
                      size="middle"
                      loading={loading}
                      scroll={{ x: 'max-content' }}
                      locale={{
                        emptyText: loading ? 'Loading...' : <p>No data found</p>,
                      }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          </Space>
        </div>
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default PurchaseOrderPage;
